import React, { useCallback, useContext, useEffect, useState } from "react";
import "../../../ProductsTab/components/Products/products.css";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Pagination,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  updateDoc,
  writeBatch,
  doc,
  addDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../../../firebase";
import { AsyncImage } from "loadable-image";
import CustomDialog from "../../../../components/CustomDialog/CustomDialog";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { debounce } from "lodash";
import { AppContext } from "../../../../store/AppContext";
import { CustomButton } from "../../../../components/CustomButton/CustomButton";
import { attachProductCounts } from "../../../../utils/categoryProducts";

const ColorButton = styled(Button)(() => ({
  color: "white",
  fontSize: "1rem",
  backgroundColor: "#001EBA",
  "&:hover": {
    backgroundColor: "#001EBA",
  },
}));

const Categories = () => {
  const { setCategoryTabInfo, categoryTabInfo } = useContext(AppContext);
  const { setHeaderData, setLoader } = useContext(AppContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const [categories, setCategories] = useState([]);
  const [pageDocs, setPageDocs] = useState([null]); // Array to hold document snapshots for each page
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalCategories, setTotalCategories] = useState(0);
  const [rearrangeDialogOpen, setRearrangeDialogOpen] = useState(false);
  const [categoriesToRearrange, setCategoriesToRearrange] = useState([]);
  const pageSize = 10;

  useEffect(() => {
    setHeaderData((prevState) => ({
      ...prevState,
      leftText: "All categories",
      back: true,
    }));
  }, []);

  const fetchTotalCategories = async () => {
    const categoriesCollectionRef = collection(db, "categories");
    const snapshot = await getDocs(categoriesCollectionRef);
    setTotalCategories(snapshot.size);
    setCategoryTabInfo((prevState) => ({
      ...prevState,
      totalPages: Math.ceil(snapshot.size / pageSize),
    }));
  };

  const fetchCategories = async (page = 1) => {
    setLoader(true);
    try {
      const categoriesCollectionRef = collection(db, "categories");
      let categoriesQuery;

      if (searchTerm) {
        // NOTE: Firestore range queries (>= / <=) on strings are case-sensitive
        // (compared by Unicode code point), so a raw `where("categoryName",
        // ">=", term)` misses different-case matches (e.g. searching "coffee"
        // won't find "Coffee") and only matches prefixes. Categories are few
        // enough to fetch all and filter client-side, giving reliable
        // case-insensitive substring matching.
        const allSnapshot = await getDocs(
          query(categoriesCollectionRef, orderBy("order"))
        );
        const term = searchTerm.toLowerCase().trim();
        const matched = allSnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter(
            (c) =>
              typeof c.categoryName === "string" &&
              c.categoryName.toLowerCase().includes(term)
          );

        const totalPages = Math.max(1, Math.ceil(matched.length / pageSize));
        setTotalCategories(matched.length);
        setCategoryTabInfo((prevState) => ({
          ...prevState,
          totalPages,
        }));

        const startIndex = (page - 1) * pageSize;
        const pageSlice = matched.slice(startIndex, startIndex + pageSize);
        const withCounts = await attachProductCounts(pageSlice, matched);
        setCategories(withCounts);
        return;
      } else {
        // For first page or when we have the previous page's last doc
        if (page === 1 || !pageDocs[page - 1]) {
          categoriesQuery = query(
            categoriesCollectionRef,
            orderBy("order"),
            limit(page * pageSize)
          );
        } else {
          categoriesQuery = query(
            categoriesCollectionRef,
            orderBy("order"),
            startAfter(pageDocs[page - 1]),
            limit(pageSize)
          );
        }
      }

      const querySnapshot = await getDocs(categoriesQuery);
      const categoriesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // If this is a direct page jump (not sequential navigation)
      let pageCategories;
      if (page > 1 && (!pageDocs[page - 1] || pageDocs.length <= page - 1)) {
        // Store all documents up to the current page
        const allDocs = querySnapshot.docs;
        const newPageDocs = [null]; // First page has no startAfter

        // Create snapshots for each page
        for (let i = 1; i < page; i++) {
          const endIndex = i * pageSize;
          if (endIndex < allDocs.length) {
            newPageDocs[i] = allDocs[endIndex - 1];
          }
        }

        setPageDocs(newPageDocs);

        // Slice the data for the current page
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        pageCategories = categoriesData.slice(startIndex, endIndex);
      } else {
        pageCategories = categoriesData;

        // Update pageDocs with the last document of this page
        if (querySnapshot.docs.length > 0) {
          const newPageDocs = [...pageDocs];
          newPageDocs[page] = querySnapshot.docs[querySnapshot.docs.length - 1];
          setPageDocs(newPageDocs);
        }
      }

      // Live product count (parent = self + subcategories), not stale products[].
      const withCounts = await attachProductCounts(pageCategories);
      setCategories(withCounts);

      // Update total count (search path returns early above)
      fetchTotalCategories();
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchTotalCategories();
    fetchCategories(categoryTabInfo?.page || 1);
  }, []);

  useEffect(() => {
    const loadPage = async () => {
      try {
        await fetchCategories(categoryTabInfo?.page || 1);
      } catch (error) {
        console.error("Error loading page:", error);
        // Fall back to page 1 if there's an error
        setCategoryTabInfo((prev) => ({ ...prev, page: 1 }));
      }
    };

    loadPage();
  }, [categoryTabInfo?.page, searchTerm, categoryTabInfo?.trigger]);

  useEffect(() => {
    if (searchTerm) {
      setCategoryTabInfo((prevState) => ({
        ...prevState,
        page: 1,
      }));
      setPageDocs([null]); // Reset page docs when searching
    }
  }, [searchTerm]);

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 500),
    []
  );

  const toggleActiveStatus = async (categoryId, currentStatus) => {
    try {
      setLoader(true);
      const categoryRef = doc(db, "categories", categoryId);
      await updateDoc(categoryRef, { active: !currentStatus });

      setCategories((prevCategories) =>
        prevCategories.map((category) =>
          category.id === categoryId
            ? { ...category, active: !currentStatus }
            : category
        )
      );
    } catch (error) {
      console.error("Error updating category status:", error);
    } finally {
      setLoader(false);
    }
  };

  const deleteCategory = async () => {
    setDialogOpen(false);
    if (!selectedCategoryId) return;
    setLoader(true);
    try {
      const categoryRef = doc(db, "categories", selectedCategoryId);
      const categorySnap = await getDocs(query(collection(db, "categories")));
      const categoryData = categorySnap.docs
        .find((d) => d.id === selectedCategoryId)
        ?.data();

      if (categoryData) {
        await addDoc(collection(db, "audit_logs"), {
          action: "delete",
          categoryId: selectedCategoryId,
          oldData: categoryData,
          timestamp: new Date(),
        });
      }

      await deleteDoc(categoryRef);
      setCategories((prev) =>
        prev.filter((category) => category.id !== selectedCategoryId)
      );
      setTotalCategories((prev) => prev - 1);
      setCategoryTabInfo((prev) => ({
        ...prev,
        totalPages: Math.ceil((totalCategories - 1) / pageSize),
      }));
    } catch (error) {
      console.error("Error deleting category:", error);
    } finally {
      setLoader(false);
      setSelectedCategoryId(null);
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const reordered = Array.from(categoriesToRearrange);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setCategoriesToRearrange(reordered);
  };

  const saveRearrangedCategories = async () => {
    setLoader(true);
    const batch = writeBatch(db);
    try {
      categoriesToRearrange.forEach((category, index) => {
        if (!category?.id) return;
        const categoryRef = doc(db, "categories", category.id);
        batch.update(categoryRef, { order: index });
      });
      await batch.commit();

      setRearrangeDialogOpen(false);
      setCategoriesToRearrange([]);
      fetchCategories(categoryTabInfo?.page || 1);
    } catch (error) {
      console.error("Error saving rearranged categories:", error);
    } finally {
      setLoader(false);
    }
  };

  const IOSSwitch = styled((props) => (
    <Switch
      focusVisibleClassName=".Mui-focusVisible"
      disableRipple
      {...props}
    />
  ))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    "& .MuiSwitch-switchBase": {
      padding: 0,
      margin: 2,
      transitionDuration: "300ms",
      "&.Mui-checked": {
        transform: "translateX(16px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          backgroundColor: " #00A3FF",
          opacity: 1,
          border: 0,
        },
        "&.Mui-disabled + .MuiSwitch-track": {
          opacity: 0.5,
        },
      },
      "&.Mui-focusVisible .MuiSwitch-thumb": {
        color: "#33cf4d",
        border: "6px solid #fff",
      },
      "&.Mui-disabled .MuiSwitch-thumb": {
        color: theme.palette.grey[100],
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.7,
      },
    },
    "& .MuiSwitch-thumb": {
      boxSizing: "border-box",
      width: 22,
      height: 22,
    },
    "& .MuiSwitch-track": {
      borderRadius: 26 / 2,
      backgroundColor: "#E9E9EA",
      opacity: 1,
      transition: theme.transitions.create(["background-color"], {
        duration: 500,
      }),
    },
  }));

  const handleCategoryClick = (data) => {
    setHeaderData((prevState) => {
      return {
        ...prevState,
        back: false,
        leftText: "Update category info",
        leftAction: () => {
          setCategoryTabInfo((prevState) => {
            return {
              ...prevState,
              addCategory: false,
              selectedCategory: null,
            };
          });
          setHeaderData({
            leftText: "",
            rightActionText: "",
            leftAction: () => {},
            rightAction: () => {},
          });
        },
        rightActionText: "Update category",
        rightAction: () => {
          setCategoryTabInfo((prevState) => {
            return {
              ...prevState,
              trigger: !prevState?.trigger,
            };
          });
        },
      };
    });
    setCategoryTabInfo((prevState) => {
      return {
        ...prevState,
        selectedCategory: data,
      };
    });
  };

  // Mobile Card Component
  const CategoryCard = ({ data }) => (
    <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 1 }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <AsyncImage
            style={{
              width: isMobile ? "60px" : "80px",
              height: isMobile ? "60px" : "80px",
              objectFit: "cover",
              borderRadius: "8px",
              flexShrink: 0,
            }}
            src={data?.images?.[0]}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              sx={{
                fontWeight: 600,
                color: "#00A3FF",
                cursor: "pointer",
                mb: 0.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              onClick={() => handleCategoryClick(data)}
            >
              {data.categoryName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {data.parentCategory?.categoryName || "No Parent"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Chip
                label={`${data?.productCount ?? data?.products?.length ?? 0} Products`}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Chip
                label={data.active ? "Active" : "Inactive"}
                size="small"
                color={data.active ? "success" : "default"}
                variant={data.active ? "filled" : "outlined"}
              />
            </Box>
          </Box>
        </Box>
      </CardContent>
      <CardActions sx={{ pt: 0, justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Status:
          </Typography>
          <IOSSwitch
            checked={data.active || false}
            onChange={() => toggleActiveStatus(data.id, data.active || false)}
            size="small"
          />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => {
              window.open(
                `https://kathys-new-alpha.vercel.app/category/${data.id}`,
                "_blank"
              );
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setSelectedCategoryId(data.id);
              setDialogOpen(true);
            }}
          >
            <DeleteOutlineOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardActions>
    </Card>
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        width: "95%",
        margin: "auto",
        padding: isMobile ? "0 8px" : "0",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? "16px" : "0",
        }}
      >
        <div style={{ width: isMobile ? "100%" : "auto" }}>
          <FormControl
            size={isMobile ? "medium" : "small"}
            variant="outlined"
            fullWidth={isMobile}
          >
            <InputLabel>Search</InputLabel>
            <OutlinedInput
              size={isMobile ? "medium" : "small"}
              onChange={(e) => debouncedSearch(e.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <SearchOutlinedIcon />
                  </IconButton>
                </InputAdornment>
              }
              label="Search"
            />
          </FormControl>
        </div>
        <div
          style={{
            display: "flex",
            gap: "10px",
            width: isMobile ? "100%" : "auto",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <CustomButton
            sx={{
              textTransform: "none",
              width: isMobile ? "100%" : "auto",
              py: isMobile ? 1.5 : 1,
            }}
            variant="outlined"
            onClick={async () => {
              setLoader(true);
              try {
                const snapshot = await getDocs(
                  query(collection(db, "categories"), orderBy("order"))
                );
                const allCategories = snapshot.docs.map((d) => ({
                  id: d.id,
                  ...d.data(),
                }));
                setCategoriesToRearrange(allCategories);
                setRearrangeDialogOpen(true);
              } catch (error) {
                console.error(
                  "Error fetching categories for rearrangement:",
                  error
                );
              } finally {
                setLoader(false);
              }
            }}
          >
            Rearrange
          </CustomButton>
          <CustomButton
            sx={{
              textTransform: "none",
              width: isMobile ? "100%" : "auto",
              py: isMobile ? 1.5 : 1,
            }}
            variant="contained"
            onClick={() => {
              setHeaderData((prevState) => {
                return {
                  ...prevState,
                  back: false,
                  leftText: "Add new category",
                  leftAction: () => {
                    setCategoryTabInfo((prevState) => {
                      return {
                        ...prevState,
                        addCategory: false,
                        selectedCategory: null,
                      };
                    });
                    setHeaderData({
                      leftText: "",
                      rightActionText: "",
                      leftAction: () => {},
                      rightAction: () => {},
                    });
                  },
                  rightActionText: "Add category",
                  rightAction: () => {
                    setCategoryTabInfo((prevState) => {
                      return {
                        ...prevState,
                        trigger: !prevState?.trigger,
                      };
                    });
                  },
                };
              });

              setCategoryTabInfo((prevState) => {
                return {
                  ...prevState,
                  addCategory: true,
                  selectedCategory: null,
                };
              });
            }}
          >
            Add new category
          </CustomButton>
        </div>
      </div>

      {/* Mobile Card View */}
      {isMobile ? (
        <div style={{ width: "100%" }}>
          {categories.map((data) => (
            <CategoryCard key={data.id} data={data} />
          ))}
        </div>
      ) : (
        /* Desktop/Tablet Table View */
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            background: "white",
            padding: isTablet ? "8px" : "10px",
            borderRadius: "8px",
            height: "max-content",
            overflowY: "scroll",
          }}
        >
          <div style={{ borderRadius: "8px", overflow: "hidden" }}>
            <table
              style={{
                width: "100%",
                fontSize: isTablet ? "13px" : "14px",
                borderCollapse: "separate",
                borderSpacing: "0 8px",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "#efefef",
                    height: "50px",
                    borderRadius: "8px",
                  }}
                >
                  <th
                    className="product-name"
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                      paddingLeft: "16px",
                    }}
                  >
                    Categories
                  </th>
                  <th
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                    }}
                  >
                    Products
                  </th>
                  <th
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                    }}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="scrollable-tbody">
                {categories.map((data) => (
                  <tr
                    key={data.id}
                    style={{
                      background: "#fff",
                      height: "70px",
                      borderRadius: "8px",
                    }}
                  >
                    <td
                      className="product-name"
                      style={{ paddingLeft: "16px" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          width: isTablet ? "120px" : "150px",
                          gap: isTablet ? "10px" : "15px",
                          alignItems: "center",
                        }}
                      >
                        <AsyncImage
                          style={{
                            width: isTablet ? "40px" : "50px",
                            height: isTablet ? "40px" : "50px",
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                          src={data?.images?.[0]}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                            alignItems: "flex-start",
                            overflow: "hidden",
                            textAlign: "start",
                          }}
                        >
                          <h5
                            style={{
                              fontWeight: "500",
                              color: "#00A3FF",
                              cursor: "pointer",
                              fontSize: isTablet ? "12px" : "14px",
                              margin: 0,
                            }}
                            onClick={() => handleCategoryClick(data)}
                          >
                            {data.categoryName}
                          </h5>
                          <h5
                            style={{
                              fontWeight: "500",
                              fontSize: isTablet ? "11px" : "14px",
                              margin: 0,
                              color: "#666",
                            }}
                          >
                            {data.parentCategory?.categoryName || "No Parent"}
                          </h5>
                        </div>
                      </div>
                    </td>
                    <td
                      className="product-price"
                      style={{ fontSize: isTablet ? "13px" : "14px" }}
                    >
                      {data?.productCount ?? data?.products?.length ?? 0}
                    </td>
                    <td className="product-action">
                      <IOSSwitch
                        checked={data.active || false}
                        onChange={() =>
                          toggleActiveStatus(data.id, data.active || false)
                        }
                      />
                    </td>
                    <td className="product-more">
                      <div
                        style={{
                          display: "flex",
                          gap: isTablet ? "10px" : "15px",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <VisibilityIcon
                          style={{
                            fontSize: isTablet ? "18px" : "20px",
                            color: "#555",
                          }}
                          onClick={() => {
                            window.open(
                              `https://kathys-new-alpha.vercel.app/category/${data.id}`,
                              "_blank"
                            );
                          }}
                        />
                        <DeleteOutlineOutlinedIcon
                          style={{
                            fontSize: isTablet ? "18px" : "20px",
                            color: "#555",
                          }}
                          onClick={() => {
                            setSelectedCategoryId(data.id);
                            setDialogOpen(true);
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalCategories > pageSize && (
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: isMobile ? "center" : "end",
            marginTop: "20px",
          }}
        >
          <Pagination
            count={categoryTabInfo?.totalPages || 1}
            page={categoryTabInfo?.page || 1}
            onChange={(e, value) => {
              // Reset to page 1 if trying to go beyond available pages
              const safePage = Math.min(
                value,
                categoryTabInfo?.totalPages || 1
              );
              setCategoryTabInfo((prevState) => ({
                ...prevState,
                page: safePage,
              }));
            }}
            color="primary"
            size={isMobile ? "medium" : "large"}
          />
        </div>
      )}

      <CustomDialog
        open={dialogOpen}
        title="Delete"
        primaryButtonText="Delete"
        onPrimaryClick={deleteCategory}
        onSecondaryClick={() => {
          setDialogOpen(false);
        }}
        description="Are you sure you want to delete this category?"
        type="error"
      />
      <CustomDialog
        open={rearrangeDialogOpen}
        title="Rearrange Categories"
        titleLeft={true}
        primaryButtonText="Save Order"
        onPrimaryClick={saveRearrangedCategories}
        onSecondaryClick={() => {
          setRearrangeDialogOpen(false);
          setCategoriesToRearrange([]);
        }}
        description={
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="categories">
              {(provided) => (
                <ul
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{ listStyleType: "none", padding: 0, margin: 0 }}
                >
                  {categoriesToRearrange.map((category, index) => (
                    <Draggable
                      key={category.id}
                      draggableId={category.id}
                      index={index}
                    >
                      {(provided) => (
                        <li
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            padding: "10px",
                            margin: "5px 0",
                            border: "1px solid #ddd",
                            borderRadius: "5px",
                            backgroundColor: "#f9f9f9",
                            display: "flex",
                            alignItems: "center",
                            gap: "15px",
                            ...provided.draggableProps.style,
                          }}
                        >
                          <DragIndicatorIcon style={{ color: "#999" }} />
                          <span style={{ fontSize: "0.8em", color: "#666" }}>
                            {index + 1}
                          </span>
                          <AsyncImage
                            style={{
                              width: "40px",
                              height: "40px",
                              objectFit: "cover",
                              borderRadius: "3px",
                            }}
                            src={category?.images?.[0]}
                          />
                          <span style={{ fontSize: ".9rem" }}>
                            {category.categoryName}
                          </span>
                          {category.parentCategory?.categoryName && (
                            <span
                              style={{ fontSize: ".75rem", color: "#999" }}
                            >
                              (Sub of {category.parentCategory.categoryName})
                            </span>
                          )}
                        </li>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
          </DragDropContext>
        }
      />
    </div>
  );
};

export default Categories;
